import { AdPlatformData, AdStrategyData } from "../../interfaces/AdStrategy";
import CommonService from "../Common";
import { AxiosRequestConfig } from "axios";

export interface ListStrategiesParams {
  name?: string;
  status?: "draft" | "active" | "archived";
  platform?: AdPlatformData;
  sortBy?: "createdAt" | "updatedAt" | "name";
  sortOrder?: "asc" | "desc";
  limit?: number;
  page?: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface StartGenerationParams {
  userId: string;
  modelAPIKey: string;
  socketId?: string;
  priority?: number;
  customInstructions?: string;
  icpId: string;
  additionalGenerationContext?: string;
  platforms?: AdPlatformData[];
}

class AdStrategyService {
  private readonly pathPrefix: string;
  private readonly commonService: CommonService;

  constructor() {
    this.pathPrefix = "/api/v1/projects";
    this.commonService = new CommonService();
  }

  // POST /api/v1/projects/:projectId/adstrategy
  async createStrategy(projectId: string | any, data: AdStrategyData) {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: `${this.pathPrefix}/${projectId}/adstrategy`,
      data,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // GET /api/v1/projects/:projectId/adstrategy
  async listStrategies(
    projectId: string | any,
    userId: string | any,
    params?: ListStrategiesParams | any
  ) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${this.pathPrefix}/${projectId}/adstrategy`,
      params: {
        projectId,
        userId,
        ...params,
      },
      withCredentials: true,
    };
    const response = await this.commonService.fetch(config);

    const strategies = response.data.strategies.map((strategy: any) => ({
      ...strategy,
      crossPlatform:
        typeof strategy.crossPlatform === "string"
          ? JSON.parse(strategy.crossPlatform)
          : strategy.crossPlatform,
      emailStrategy:
        typeof strategy.emailStrategy === "string"
          ? JSON.parse(strategy.emailStrategy)
          : strategy.emailStrategy,
      influencerMarketing:
        typeof strategy.influencerMarketing === "string"
          ? JSON.parse(strategy.influencerMarketing)
          : strategy.influencerMarketing,
      affiliateMarketing:
        typeof strategy.affiliateMarketing === "string"
          ? JSON.parse(strategy.affiliateMarketing)
          : strategy.affiliateMarketing,
      growthStrategy:
        typeof strategy.growthStrategy === "string"
          ? JSON.parse(strategy.growthStrategy)
          : strategy.growthStrategy,
      platforms: {
        googleAds:
          typeof strategy.platforms.googleAds === "string"
            ? JSON.parse(strategy.platforms.googleAds)
            : strategy.platforms.googleAds,
        facebookAds:
          typeof strategy.platforms.facebookAds === "string"
            ? JSON.parse(strategy.platforms.facebookAds)
            : strategy.platforms.facebookAds,
        youtubeAds:
          typeof strategy.platforms.youtubeAds === "string"
            ? JSON.parse(strategy.platforms.youtubeAds)
            : strategy.platforms.youtubeAds,
        instagramAds:
          typeof strategy.platforms.instagramAds === "string"
            ? JSON.parse(strategy.platforms.instagramAds)
            : strategy.platforms.instagramAds,
        tiktokAds:
          typeof strategy.platforms.tiktokAds === "string"
            ? JSON.parse(strategy.platforms.tiktokAds)
            : strategy.platforms.tiktokAds,
        linkedinAds:
          typeof strategy.platforms.linkedinAds === "string"
            ? JSON.parse(strategy.platforms.linkedinAds)
            : strategy.platforms.linkedinAds,
        snapchatAds:
          typeof strategy.platforms.snapchatAds === "string"
            ? JSON.parse(strategy.platforms.snapchatAds)
            : strategy.platforms.snapchatAds,
        xAds:
          typeof strategy.platforms.xAds === "string"
            ? JSON.parse(strategy.platforms.xAds)
            : strategy.platforms.xAds,
        redditAds:
          typeof strategy.platforms.redditAds === "string"
            ? JSON.parse(strategy.platforms.redditAds)
            : strategy.platforms.redditAds,
      },
    }));

    return {
      ...response.data,
      strategies,
    };
  }

  // GET /api/v1/projects/:projectId/adstrategy/:strategyId
  async getStrategy(projectId: string | any, strategyId: string | any) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${this.pathPrefix}/${projectId}/adstrategy/${strategyId}`,
      withCredentials: true,
    };
    const strategy = await this.commonService.fetch(config);

    return {
      ...strategy,
      crossPlatform:
        typeof strategy.crossPlatform === "string"
          ? JSON.parse(strategy.crossPlatform)
          : strategy.crossPlatform,
      emailStrategy:
        typeof strategy.emailStrategy === "string"
          ? JSON.parse(strategy.emailStrategy)
          : strategy.emailStrategy,
      influencerMarketing:
        typeof strategy.influencerMarketing === "string"
          ? JSON.parse(strategy.influencerMarketing)
          : strategy.influencerMarketing,
      affiliateMarketing:
        typeof strategy.affiliateMarketing === "string"
          ? JSON.parse(strategy.affiliateMarketing)
          : strategy.affiliateMarketing,
      growthStrategy:
        typeof strategy.growthStrategy === "string"
          ? JSON.parse(strategy.growthStrategy)
          : strategy.growthStrategy,
      platforms: {
        googleAds:
          typeof strategy.platforms.googleAds === "string"
            ? JSON.parse(strategy.platforms.googleAds)
            : strategy.platforms.googleAds,
        facebookAds:
          typeof strategy.platforms.facebookAds === "string"
            ? JSON.parse(strategy.platforms.facebookAds)
            : strategy.platforms.facebookAds,
        youtubeAds:
          typeof strategy.platforms.youtubeAds === "string"
            ? JSON.parse(strategy.platforms.youtubeAds)
            : strategy.platforms.youtubeAds,
        instagramAds:
          typeof strategy.platforms.instagramAds === "string"
            ? JSON.parse(strategy.platforms.instagramAds)
            : strategy.platforms.instagramAds,
        tiktokAds:
          typeof strategy.platforms.tiktokAds === "string"
            ? JSON.parse(strategy.platforms.tiktokAds)
            : strategy.platforms.tiktokAds,
        linkedinAds:
          typeof strategy.platforms.linkedinAds === "string"
            ? JSON.parse(strategy.platforms.linkedinAds)
            : strategy.platforms.linkedinAds,
        snapchatAds:
          typeof strategy.platforms.snapchatAds === "string"
            ? JSON.parse(strategy.platforms.snapchatAds)
            : strategy.platforms.snapchatAds,
        xAds:
          typeof strategy.platforms.xAds === "string"
            ? JSON.parse(strategy.platforms.xAds)
            : strategy.platforms.xAds,
        redditAds:
          typeof strategy.platforms.redditAds === "string"
            ? JSON.parse(strategy.platforms.redditAds)
            : strategy.platforms.redditAds,
      },
    };
  }

  // PUT /api/v1/projects/:projectId/adstrategy/:strategyId
  async updateStrategy(
    projectId: string | any,
    strategyId: string | any,
    data: Partial<Pick<AdStrategyData, "name" | "platforms" | "metadata">>
  ) {
    const processedData = {
      ...data,
      platforms: data.platforms
        ? {
            googleAds: data.platforms.googleAds
              ? JSON.stringify(data.platforms.googleAds)
              : undefined,
            facebookAds: data.platforms.facebookAds
              ? JSON.stringify(data.platforms.facebookAds)
              : undefined,
            youtubeAds: data.platforms.youtubeAds
              ? JSON.stringify(data.platforms.youtubeAds)
              : undefined,
            instagramAds: data.platforms.instagramAds
              ? JSON.stringify(data.platforms.instagramAds)
              : undefined,
            tiktokAds: data.platforms.tiktokAds
              ? JSON.stringify(data.platforms.tiktokAds)
              : undefined,
            linkedinAds: data.platforms.linkedinAds
              ? JSON.stringify(data.platforms.linkedinAds)
              : undefined,
            snapchatAds: data.platforms.snapchatAds
              ? JSON.stringify(data.platforms.snapchatAds)
              : undefined,
            xAds: data.platforms.xAds
              ? JSON.stringify(data.platforms.xAds)
              : undefined,
            redditAds: data.platforms.redditAds
              ? JSON.stringify(data.platforms.redditAds)
              : undefined,
          }
        : undefined,
    };

    const config: AxiosRequestConfig = {
      method: "PUT",
      url: `${this.pathPrefix}/${projectId}/adstrategy/${strategyId}`,
      data: processedData,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // DELETE /api/v1/projects/:projectId/adstrategy/:strategyId
  async deleteStrategy(projectId: string | any, strategyId: string | any) {
    const config: AxiosRequestConfig = {
      method: "DELETE",
      url: `${this.pathPrefix}/${projectId}/adstrategy/${strategyId}`,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // POST /api/v1/projects/:projectId/adstrategy/generate
  async startStrategyGeneration(
    projectId: string | any,
    params: StartGenerationParams
  ) {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: `${this.pathPrefix}/${projectId}/adstrategy/generate`,
      data: params,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // POST /api/v1/projects/:projectId/adstrategy/status
  async getGenerationStatus(
    projectId: string | any,
    data: { userId: string; jobId?: string }
  ) {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: `${this.pathPrefix}/${projectId}/adstrategy/status`,
      data,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // GET /api/v1/projects/:projectId/adstrategy/activity/:jobId?
  async getActivityLog(
    projectId: string | any,
    params: { jobId?: string; page?: number; limit?: number }
  ) {
    const queryParams = new URLSearchParams({
      ...(params.page && { page: params.page.toString() }),
      ...(params.limit && { limit: params.limit.toString() }),
    });

    const url = params.jobId
      ? `${this.pathPrefix}/${projectId}/adstrategy/activity/${params.jobId}`
      : `${this.pathPrefix}/${projectId}/adstrategy/activity`;

    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${url}?${queryParams}`,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // POST /api/v1/projects/:projectId/adstrategy/:strategyId/qa
  async answerQuestionOnDocument(
    userId: string | any,
    projectId: string | any,
    strategyId: string | any,
    question: string,
    modelAPIKey: string | any
  ) {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: `${this.pathPrefix}/${projectId}/adstrategy/${strategyId}/qa`,
      data: { question, userId, modelAPIKey },
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }
}

export default AdStrategyService;
